import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Search,
  Clear,
  Devices,
  ContactPhone,
} from '@mui/icons-material';
import { THEME_COLORS, BREAKPOINTS } from '../../constants';

/**
 * Componente reutilizable de barra de búsqueda
 */
const SearchBar = ({
  searchInput,
  searchTerm,
  searchType = 'device',
  placeholder = 'Buscar...',
  onSearchInputChange,
  onSearch,
  onClear,
  onKeyPress,
  onSearchTypeChange,
  showSearchType = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.MOBILE));

  // Placeholders dinámicos
  const getPlaceholder = () => {
    if (placeholder !== 'Buscar...') return placeholder;

    switch (searchType) {
      case 'contact':
        return 'Buscar por nombre o teléfono del contacto...';
      case 'device':
      default:
        return 'Buscar dispositivos por nombre o ID...';
    }
  };

  return (
    <Box sx={{ marginBottom: 4 }}>
      {/* Campo de búsqueda */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        {/* Selector de tipo de búsqueda */}
        {showSearchType && (
          <FormControl sx={{ minWidth: 160 }}>
            <Select
              value={searchType}
              onChange={(e) => onSearchTypeChange && onSearchTypeChange(e.target.value)}
              displayEmpty
              sx={{
                height: 48,
                '& .MuiSelect-select': {
                  paddingRight: '32px !important',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: THEME_COLORS.BORDER,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: THEME_COLORS.BORDER_HOVER,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: THEME_COLORS.PRIMARY,
                },
              }}
            >
              <MenuItem value="device">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Devices sx={{ fontSize: '1.2rem', color: THEME_COLORS.SECONDARY }} />
                  <Typography>Dispositivo</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="contact">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ContactPhone sx={{ fontSize: '1.2rem', color: THEME_COLORS.SECONDARY }} />
                  <Typography>Contacto</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          placeholder={getPlaceholder()}
          value={searchInput}
          onChange={(e) => onSearchInputChange(e.target.value)}
          onKeyPress={onKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {showSearchType ? (
                  searchType === 'contact' ? (
                    <ContactPhone sx={{ color: THEME_COLORS.SECONDARY }} />
                  ) : (
                    <Devices sx={{ color: THEME_COLORS.SECONDARY }} />
                  )
                ) : (
                  <Search sx={{ color: THEME_COLORS.SECONDARY }} />
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: 48,
              borderColor: THEME_COLORS.BORDER,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: THEME_COLORS.BORDER_HOVER,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: THEME_COLORS.PRIMARY,
              },
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, minWidth: { xs: 'auto', sm: 'fit-content' }, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={onSearch}
            sx={{
              height: 48,
              minWidth: 120,
              width: { xs: '100%', sm: 'auto' },
              backgroundColor: THEME_COLORS.PRIMARY,
              '&:hover': {
                backgroundColor: THEME_COLORS.TEXT_SECONDARY,
              },
            }}
          >
            Buscar
          </Button>
          {searchTerm && (
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={onClear}
              sx={{
                height: 48,
                minWidth: 100,
                width: { xs: '100%', sm: 'auto' },
                borderColor: THEME_COLORS.BORDER,
                color: THEME_COLORS.SECONDARY,
                '&:hover': {
                  borderColor: THEME_COLORS.BORDER_HOVER,
                  backgroundColor: THEME_COLORS.LIGHT_GRAY,
                },
              }}
            >
              Limpiar
            </Button>
          )}
        </Box>
      </Box>

      {/* Indicador de búsqueda activa */}
      {searchTerm && (
        <Box sx={{ marginTop: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: THEME_COLORS.TEXT_MUTED,
              fontSize: '0.875rem',
              fontStyle: 'italic',
            }}
          >
            Mostrando resultados para: "<strong>{searchTerm}</strong>"
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SearchBar; 